import styled from 'styled-components'
import {
  LEGACY_BREAKPOINT_QUERIES,
  createResponsiveBreakpointQuery
} from 'styles/breakpoints'

const tabletDownQuery = createResponsiveBreakpointQuery('tabletDown', {
  legacyQuery: LEGACY_BREAKPOINT_QUERIES.tabletDown
})

const getButtonSize = ({ $size }) => `${$size || 38}px`

export const PlaybarWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: calc(100% - 48px);
  min-height: 100px;
  margin: 0 24px 24px;
  padding: 16px 24px;
  background-color: ${({ theme }) => theme.colors.neutralW20};
  border: 2px solid ${({ theme }) => theme.colors.neutralW30};
  border-radius: 40px;
  position: relative;
  z-index: 2;
  overflow: visible;
  pointer-events: auto;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.24);

  @media ${tabletDownQuery} {
    gap: 24px;
    width: calc(100% - 32px);
    min-height: 72px;
    margin: 0 16px 16px;
    padding: 16px;
    border-width: 1px;
    border-radius: 24px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  }
`

export const ReleaseDetailsWrapper = styled.button`
  display: flex;
  flex: 1 1 0;
  gap: 16px;
  align-items: center;
  min-width: 0;
  padding: 0;
  color: inherit;
  background: transparent;
  border: 0;
  font: inherit;
  text-align: left;
  appearance: none;
  cursor: ${({ $canOpenRelease }) =>
    $canOpenRelease ? 'pointer' : 'default'};

  @media ${tabletDownQuery} {
    flex: 1 1 auto;
    gap: 8px;
  }
`

export const CoverArt = styled.img`
  flex: 0 0 auto;
  width: ${({ $isCompact }) => ($isCompact ? '40px' : '64px')};
  height: ${({ $isCompact }) => ($isCompact ? '40px' : '64px')};
  border: 1px solid ${({ theme }) => theme.colors.neutralW40};
  border-radius: 5px;
  object-fit: cover;

  @media ${tabletDownQuery} {
    border-width: 0.5px;
    border-radius: 3.75px;
  }
`

export const ReleaseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`

export const ReleaseTitle = styled.span`
  display: block;
  max-width: 100%;
  margin-bottom: 4px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutralW90};
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${ReleaseDetailsWrapper}[data-can-open-release='true']:hover &,
  ${ReleaseDetailsWrapper}[data-can-open-release='true']:focus-visible & {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  @media ${tabletDownQuery} {
    margin-bottom: 2px;
    color: ${({ theme }) => theme.colors.neutralW100};
    font-size: 14px;
    line-height: normal;
  }
`

export const ReleaseArtist = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  padding-bottom: 2px;
  margin-bottom: -2px;
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${ReleaseDetailsWrapper}[data-can-open-release='true']:hover &,
  ${ReleaseDetailsWrapper}[data-can-open-release='true']:focus-visible & {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  @media ${tabletDownQuery} {
    font-size: 12px;
    line-height: normal;
  }
`

export const PlayerDetailsWrapper = styled.div`
  display: flex;
  flex: 0 1 620px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  max-width: 620px;
  gap: 8px;

`

export const PlayOptionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $isDesktop }) => ($isDesktop ? '16px' : '8px')};
`

export const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

export const SliderWrapper = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 80px;
  height: 8px;
  margin: 0;
  padding: 0;

  .rc-slider {
    height: 8px;
    padding: 0;
  }

  .rc-slider-rail,
  .rc-slider-track {
    height: 8px;
    top: 0;
    border-radius: 999px;
  }

  .rc-slider-handle {
    top: 4px;
    margin-top: -6px;
    box-shadow: none;
  }
`

export const TimelinePreviewLabel = styled.span`
  position: absolute;
  top: -38px;
  left: ${({ $percentage }) => `${$percentage}%`};
  z-index: 4;
  min-width: 44px;
  padding: 4px 8px;
  color: ${({ theme }) => theme.colors.neutralW20};
  background-color: ${({ theme }) => theme.colors.neutralW100};
  border-radius: 5px;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  transform: translateX(-50%);
  pointer-events: none;
`

export const TimelinePreviewMarker = styled.span`
  position: absolute;
  top: -4px;
  left: ${({ $percentage }) => `${$percentage}%`};
  z-index: 3;
  width: 2px;
  height: 16px;
  background-color: ${({ theme }) => theme.colors.player50};
  border-radius: 999px;
  transform: translateX(-50%);
  pointer-events: none;
`

export const DurationWrapper = styled.span`
  flex: 0 0 auto;
  min-width: 28px;
  overflow: hidden;
  color: ${({ theme, $hasNoSong }) =>
    $hasNoSong ? theme.colors.neutralW40 : theme.colors.neutralW70};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const OptionsWrapper = styled.div`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`

export const IconButtonFrame = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: ${({ $disabled, $primary, theme }) => {
    if ($primary) return theme.colors.neutralW20
    return $disabled ? theme.colors.neutralW40 : theme.colors.neutralW100
  }};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: auto;
    background: transparent;
    border-radius: 50%;
    font-size: inherit;
    font-weight: inherit;
    letter-spacing: 0;
    line-height: 1;
  }

  button {
    width: ${getButtonSize};
    min-width: ${getButtonSize};
    height: ${getButtonSize};
    padding: ${({ $primary }) => ($primary ? '8px' : '8.2px')};
    color: currentColor;
    background-color: ${({ $active, $primary, theme }) => {
      if ($primary) return theme.colors.neutralW100
      return $active ? theme.colors.neutralW30 : 'transparent'
    }} !important;
    border: 0;
    border-radius: 50%;
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  }

  button:hover {
    background-color: ${({ $disabled, $primary, theme }) => {
      if ($disabled) return $primary ? theme.colors.neutralW100 : 'transparent'
      return $primary ? theme.colors.neutralW80 : theme.colors.neutralW30
    }} !important;
  }

  button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.interact70};
  }

  svg {
    display: block;
    flex: 0 0 auto;
    pointer-events: none;
  }
`

export const VolumePopover = styled.div`
  box-sizing: border-box;
  position: absolute;
  right: 5px;
  bottom: calc(100% + 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 156px;
  padding: 8px 10px;
  background-color: ${({ theme }) => theme.colors.neutralW20};
  border: 1px solid ${({ theme }) => theme.colors.neutralW30};
  border-radius: 37px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.25);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transform: translateY(4px);
  transition:
    opacity 120ms ease,
    transform 120ms ease;
  z-index: 5;

  ${({ $isOpen }) =>
    $isOpen &&
    `
      transform: translateY(0);
    `}

  &::after {
    position: absolute;
    right: -6px;
    bottom: -10px;
    width: 40px;
    height: 10px;
    content: '';
  }
`

export const VolumeControlWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

export const VolumeRangeFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 8px;
  height: 140px;
`

export const VolumeRange = styled.input`
  flex: 0 0 auto;
  width: 140px;
  height: 8px;
  margin: 0;
  border: 0;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.neutralW30};
  background-image: ${({ theme }) =>
    `linear-gradient(${theme.colors.neutralW100}, ${theme.colors.neutralW100})`};
  background-repeat: no-repeat;
  cursor: pointer;
  transform: rotate(-90deg);
  appearance: none;

  &:hover {
    background-image: ${({ $isMuted, theme }) =>
      `linear-gradient(${
        $isMuted ? theme.colors.neutralW100 : theme.colors.player50
      }, ${$isMuted ? theme.colors.neutralW100 : theme.colors.player50})`};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.interact70};
  }

  &::-webkit-slider-runnable-track {
    height: 8px;
    border: 0;
    border-radius: 999px;
    background: transparent;
  }

  &::-webkit-slider-thumb {
    width: 10px;
    height: 10px;
    margin-top: -1px;
    border: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.neutralW100};
    cursor: pointer;
    appearance: none;
  }

  &::-moz-range-track {
    height: 8px;
    border: 0;
    border-radius: 999px;
    background: transparent;
  }

  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.neutralW100};
    cursor: pointer;
  }
`

export const ScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

export const IconContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const PlayerIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const SkipPreviousIcon = styled.span`
  display: flex;
`

export const SkipNextIcon = styled.span`
  display: flex;
`

export const IconWrapper = styled.span`
  display: flex;
`

export const VolumeSliderWrapper = styled.div`
  display: flex;
`

export const Wrap = styled.span`
  margin: 0 8px;
`

export const ToggleContainer = styled.div`
  display: flex;
  align-self: center;
  padding-top: 5px;
  align-items: center;
  justify-content: center;

  input:checked + label {
    &:after {
      width: 15px !important;
      height: 15px !important;
      margin-left: 19px !important;
    }

    div {
      left: 18px !important;
      top: 1px;
    }
    &:hover {
      background-color: ${({ theme }) => `${theme.colors.player40}`};
    }
  }

  label {
    &:after {
      width: 15px;
      height: 15px;
      margin: 2px 0 3px 3px;
    }

    div {
      left: 2px !important;
      top: 1px;
    }
  }

  svg {
    width: 14px !important;
    height: 14px !important;
  }
`
